/**
 * CourtierPortalPage - Portail Courtier (Alexandre / Artemis Courtage).
 *
 * Acces uniquement pour les utilisateurs dont le role est 'courtier'.
 * Liste les projets ou il a ete invite + detail edulcore + documents client.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import CourtierClientsTab from '../components/CourtierClientsTab';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n) => {
  if (n == null) return '—';
  return Math.round(n).toLocaleString('fr-FR') + ' EUR';
};

const statusLabels = {
  initiation: 'Initiation',
  acquisition: 'Acquisition',
  notaire: 'Notaire',
  administratif: 'Administratif',
  travaux: 'Travaux',
  livraison: 'Livraison',
  termine: 'Termine',
};

const CourtierPortalPage = ({ user, onLogout }) => {
  const [activeTab, setActiveTab] = useState('projects'); // 'projects' | 'clients'
  const [projects, setProjects] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [docs, setDocs] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [noteDraft, setNoteDraft] = useState('');
  const [verdictDraft, setVerdictDraft] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [noteFeedback, setNoteFeedback] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);

  const loadProjects = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/courtier/projects`);
      setProjects(r.data || []);
      if ((r.data || []).length === 1) {
        setSelectedId(r.data[0].project_id);
      }
    } catch {
      setProjects([]);
    } finally { setLoadingList(false); }
  }, []);

  useEffect(() => { loadProjects(); }, [loadProjects]);

  const loadDetail = useCallback(async (pid) => {
    if (!pid) return;
    setLoadingDetail(true);
    setNoteFeedback(null);
    try {
      const [d, docResp] = await Promise.all([
        axios.get(`${API}/courtier/projects/${pid}`),
        axios.get(`${API}/courtier/projects/${pid}/client-documents`),
      ]);
      setDetail(d.data);
      setDocs(docResp.data);
      setNoteDraft((d.data?.courtier_note?.text) || '');
      setVerdictDraft((d.data?.courtier_note?.verdict) || '');
    } catch {
      setDetail(null);
      setDocs(null);
      setNoteDraft('');
      setVerdictDraft('');
    } finally {
      setLoadingDetail(false);
    }
  }, []);

  const saveNote = async () => {
    if (!selectedId) return;
    setSavingNote(true);
    setNoteFeedback(null);
    try {
      const r = await axios.put(`${API}/courtier/projects/${selectedId}/note`, {
        note: noteDraft,
        verdict: verdictDraft || null,
      });
      setDetail({ ...detail, courtier_note: r.data.courtier_note });
      setNoteFeedback({ type: 'success', msg: 'Verdict enregistre. L\'equipe RealEase est notifiee.' });
    } catch (e) {
      setNoteFeedback({ type: 'error', msg: e.response?.data?.detail || 'Echec enregistrement' });
    } finally { setSavingNote(false); }
  };

  useEffect(() => { if (selectedId) loadDetail(selectedId); }, [selectedId, loadDetail]);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="courtier-portal">
      <header className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src="/realease-logo.jpg" alt="RealEase" className="h-9 object-contain" />
          <span className="text-sm font-semibold text-gray-700 hidden sm:inline">| Espace courtier</span>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-600">{user?.name || user?.email}</span>
          <button
            onClick={onLogout}
            className="text-sm text-gray-500 hover:text-gray-700"
            data-testid="courtier-logout-btn"
          >
            Deconnexion
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-6">
        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-gray-200" data-testid="courtier-portal-tabs">
          <button
            type="button"
            onClick={() => setActiveTab('projects')}
            className={`px-4 py-2 text-sm font-semibold border-b-2 transition ${
              activeTab === 'projects'
                ? 'border-[#13736D] text-[#13736D]'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            data-testid="courtier-tab-projects"
          >
            Projets ({projects.length})
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('clients')}
            className={`px-4 py-2 text-sm font-semibold border-b-2 transition ${
              activeTab === 'clients'
                ? 'border-[#13736D] text-[#13736D]'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            data-testid="courtier-tab-clients"
          >
            Clients
          </button>
        </div>

        {activeTab === 'clients' ? (
          <CourtierClientsTab />
        ) : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Sidebar : liste projets */}
        <aside className="lg:col-span-3" data-testid="courtier-projects-list">
          <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-3">Mes dossiers</h2>
          {loadingList ? (
            <div className="text-sm text-gray-400 italic">Chargement...</div>
          ) : projects.length === 0 ? (
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <p className="text-sm text-gray-500">
                Aucun dossier ne vous a encore ete partage par l'equipe RealEase.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {projects.map((p) => (
                <button
                  key={p.project_id}
                  type="button"
                  onClick={() => setSelectedId(p.project_id)}
                  className={`w-full text-left p-3 rounded-lg border transition ${
                    selectedId === p.project_id
                      ? 'bg-[#13736D] text-white border-[#13736D]'
                      : 'bg-white border-gray-200 hover:border-[#13736D]'
                  }`}
                  data-testid={`courtier-project-item-${p.project_id}`}
                >
                  <p className="font-semibold text-sm truncate">{p.name}</p>
                  <p className={`text-xs mt-0.5 ${selectedId === p.project_id ? 'text-white/80' : 'text-gray-500'}`}>
                    {p.client_name || 'Client non renseigne'}
                  </p>
                  <p className={`text-xs ${selectedId === p.project_id ? 'text-white/70' : 'text-gray-400'}`}>
                    {statusLabels[p.status] || p.status}
                  </p>
                </button>
              ))}
            </div>
          )}
        </aside>

        {/* Detail */}
        <main className="lg:col-span-9 space-y-6">
          {!selectedId ? (
            <div className="bg-white rounded-lg border border-gray-200 p-12 text-center text-gray-400">
              <svg className="w-16 h-16 mx-auto text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17v-6h13M5 11V5a2 2 0 012-2h10a2 2 0 012 2v4M5 11h14M3 21h18" />
              </svg>
              <p>Selectionnez un dossier dans la liste pour voir le detail.</p>
            </div>
          ) : loadingDetail || !detail ? (
            <div className="bg-white rounded-lg border border-gray-200 p-12 text-center text-gray-400">
              Chargement du dossier...
            </div>
          ) : (
            <>
              {/* Header projet */}
              <div className="bg-white rounded-lg border border-gray-200 p-5" data-testid="courtier-detail-header">
                <h1 className="text-2xl font-bold text-gray-900">{detail.name}</h1>
                <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600">
                  <span>{detail.client_name || '—'}</span>
                  {detail.city && <span>{detail.city}{detail.postal_code ? ` (${detail.postal_code})` : ''}</span>}
                  <span className="text-[#A57F4F] font-semibold">{statusLabels[detail.status] || detail.status}</span>
                </div>
                {detail.address && <p className="text-xs text-gray-500 mt-1">{detail.address}</p>}
              </div>

              {/* Chiffres clefs */}
              <div className="bg-white rounded-lg border border-gray-200 p-5" data-testid="courtier-detail-summary">
                <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Chiffres clefs du projet</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                  <div className="p-3 rounded-lg bg-[#13736D]/5 border border-[#13736D]/10">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-[#13736D]">Total projet TTC</p>
                    <p className="text-xl font-bold text-[#13736D] mt-1">{fmt(detail.summary?.total_project_ttc)}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-[#A57F4F]/5 border border-[#A57F4F]/10">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-[#A57F4F]">Montant a emprunter</p>
                    <p className="text-xl font-bold text-[#A57F4F] mt-1">{fmt(detail.summary?.loan_amount)}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-gray-50 border border-gray-100">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-600">Apport personnel</p>
                    <p className="text-xl font-bold text-gray-800 mt-1">{fmt(detail.summary?.apport)}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-gray-50 border border-gray-100">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-600">Travaux TTC</p>
                    <p className="text-xl font-bold text-gray-800 mt-1">{fmt(detail.summary?.travaux_total_ttc)}</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-0.5 text-sm">
                  <div className="flex justify-between border-b border-gray-100 py-1">
                    <span className="text-gray-600">Prix net foncier</span>
                    <span className="font-semibold text-gray-900">{fmt(detail.summary?.foncier_net)}</span>
                  </div>
                  <div className="flex justify-between border-b border-gray-100 py-1">
                    <span className="text-gray-600">Frais d'agence</span>
                    <span className="font-semibold text-gray-900">{fmt(detail.summary?.frais_agence)}</span>
                  </div>
                  <div className="flex justify-between border-b border-gray-100 py-1">
                    <span className="text-gray-600">Frais de notaire</span>
                    <span className="font-semibold text-gray-900">{fmt(detail.summary?.frais_notaire)}</span>
                  </div>
                  <div className="flex justify-between border-b border-gray-100 py-1">
                    <span className="text-gray-600">Garantie hypothecaire</span>
                    <span className="font-semibold text-gray-900">{fmt(detail.summary?.garantie_hypothecaire)}</span>
                  </div>
                  <div className="flex justify-between border-b border-gray-100 py-1">
                    <span className="text-gray-600">Frais de dossier bancaire</span>
                    <span className="font-semibold text-gray-900">{fmt(detail.summary?.frais_dossier_bancaire)}</span>
                  </div>
                  <div className="flex justify-between border-b border-gray-100 py-1">
                    <span className="text-gray-600">Frais de courtage</span>
                    <span className="font-semibold text-gray-900">{fmt(detail.summary?.frais_courtage)}</span>
                  </div>
                  <div className="flex justify-between border-b border-gray-100 py-1">
                    <span className="text-gray-600">Honoraires RealEase</span>
                    <span className="font-semibold text-[#13736D]">{fmt(detail.summary?.honoraires_realease)}</span>
                  </div>
                </div>
              </div>

              {/* Note d'analyse courtier (privee, visible par l'equipe RealEase) */}
              <div className="bg-white rounded-lg border border-gray-200 p-5" data-testid="courtier-note-panel">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-2">
                      <svg className="w-4 h-4 text-[#A57F4F]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      Mon analyse / verdict de financement
                    </h2>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Privee, visible par l'equipe RealEase uniquement.
                      {detail.courtier_note?.updated_at && (
                        <span className="ml-2 text-gray-400">
                          Derniere mise a jour : {new Date(detail.courtier_note.updated_at).toLocaleString('fr-FR')}
                        </span>
                      )}
                    </p>
                  </div>
                </div>
                <textarea
                  value={noteDraft}
                  onChange={(e) => setNoteDraft(e.target.value)}
                  placeholder="Ex: Dossier solide, capacite d'emprunt 280k confirmee. Recommande BNP a 3.45% sur 25 ans. Garantie hypothecaire necessaire vu la SCI..."
                  rows={5}
                  className="w-full text-sm border border-gray-200 rounded-md p-3 focus:outline-none focus:ring-2 focus:ring-[#13736D]/30 focus:border-[#13736D]"
                  data-testid="courtier-note-textarea"
                />
                <div className="mt-3">
                  <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Verdict</p>
                  <div className="flex flex-wrap gap-2" data-testid="courtier-verdict-chips">
                    {[
                      { v: 'favorable',   label: 'Favorable',   color: 'emerald' },
                      { v: 'a_etudier',   label: 'A etudier',   color: 'amber'   },
                      { v: 'defavorable', label: 'Defavorable', color: 'red'     },
                    ].map(o => {
                      const active = verdictDraft === o.v;
                      const colorMap = {
                        emerald: active ? 'bg-emerald-600 border-emerald-600 text-white' : 'bg-white border-emerald-300 text-emerald-700 hover:bg-emerald-50',
                        amber:   active ? 'bg-amber-500 border-amber-500 text-white'     : 'bg-white border-amber-300 text-amber-700 hover:bg-amber-50',
                        red:     active ? 'bg-red-600 border-red-600 text-white'         : 'bg-white border-red-300 text-red-700 hover:bg-red-50',
                      };
                      return (
                        <button
                          key={o.v}
                          type="button"
                          onClick={() => setVerdictDraft(active ? '' : o.v)}
                          className={`px-3 py-1.5 rounded-full border text-xs font-semibold transition ${colorMap[o.color]}`}
                          data-testid={`courtier-verdict-${o.v}`}
                        >
                          {o.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="flex items-center justify-between mt-2">
                  {noteFeedback && (
                    <p className={`text-xs ${noteFeedback.type === 'success' ? 'text-emerald-700' : 'text-red-600'}`} data-testid="courtier-note-feedback">
                      {noteFeedback.msg}
                    </p>
                  )}
                  <button
                    type="button"
                    onClick={saveNote}
                    disabled={savingNote}
                    className="ml-auto px-4 py-2 rounded-md text-sm font-semibold bg-[#13736D] text-white hover:bg-[#0f5e58] disabled:opacity-50 transition"
                    data-testid="courtier-note-save"
                  >
                    {savingNote ? 'Enregistrement...' : 'Enregistrer ma note'}
                  </button>
                </div>
              </div>

              {/* Documents client */}
              <div className="bg-white rounded-lg border border-gray-200 p-5" data-testid="courtier-client-docs">                <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                  Pieces justificatives client ({docs?.files?.length || 0})
                </h2>
                {(!docs || (docs.files || []).length === 0) ? (
                  <p className="text-sm text-gray-400 italic">
                    Aucune piece n'a encore ete deposee par le client.
                  </p>
                ) : (
                  <div className="space-y-1.5">
                    {(docs.files || []).map((f) => (
                      <div
                        key={f.file_id}
                        className="flex items-center gap-3 p-2 rounded-md bg-gray-50 hover:bg-gray-100 transition text-sm"
                        data-testid={`courtier-doc-${f.file_id}`}
                      >
                        <svg className="w-4 h-4 text-[#13736D] shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 truncate">{f.filename}</p>
                          <p className="text-xs text-gray-500">{f.folder}</p>
                        </div>
                        <button
                          type="button"
                          onClick={async () => {
                            setDownloadingId(f.file_id);
                            try {
                              const resp = await axios.get(`${API}/courtier/documents/${f.file_id}/download`, { responseType: 'blob' });
                              const url = URL.createObjectURL(new Blob([resp.data]));
                              const a = document.createElement('a');
                              a.href = url; a.download = f.filename || `document-${f.file_id}`;
                              document.body.appendChild(a); a.click(); a.remove();
                              setTimeout(() => URL.revokeObjectURL(url), 500);
                            } catch (e) {
                              alert(e.response?.data?.detail || 'Telechargement impossible');
                            } finally { setDownloadingId(null); }
                          }}
                          disabled={downloadingId === f.file_id}
                          className="px-3 py-1.5 rounded-md text-xs font-semibold bg-[#13736D] text-white hover:bg-[#0f5e58] disabled:opacity-50 transition shrink-0"
                          data-testid={`courtier-download-${f.file_id}`}
                        >
                          {downloadingId === f.file_id ? '...' : 'Telecharger'}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Interventions travaux (sans cost_ht) */}
              {detail.interventions && detail.interventions.length > 0 && (
                <div className="bg-white rounded-lg border border-gray-200 p-5" data-testid="courtier-interventions">
                  <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                    Intervenants travaux ({detail.interventions.length})
                  </h2>
                  <div className="space-y-1.5">
                    {detail.interventions.map((it, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 rounded-md bg-gray-50 text-sm">
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-gray-800 truncate">{it.intervenant}</p>
                          {it.description && <p className="text-xs text-gray-500 truncate">{it.description}</p>}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="font-medium text-gray-700">{fmt(it.cost_ttc)}</span>
                          <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${
                            it.paid ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                          }`}>
                            {it.paid ? 'Paye' : 'En attente'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </main>
        </div>
        )}
      </div>
    </div>
  );
};

export default CourtierPortalPage;
